import { useState, useEffect } from 'react';

/**
 * Custom hook for detecting mobile devices and screen sizes
 * Provides responsive breakpoints and device type detection
 */
export const useMobileDetect = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [screenSize, setScreenSize] = useState('sm');
  const [orientation, setOrientation] = useState('portrait');
  const [isTouch, setIsTouch] = useState(false);

  // Breakpoints matching Material-UI standards
  const breakpoints = {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536
  };

  // Mobile-first breakpoints for better granularity
  const mobileBreakpoints = {
    mobile: 320,
    mobileLg: 480,
    tablet: 768,
    tabletLg: 1024,
    desktop: 1200,
    desktopLg: 1440
  };

  const getUserAgent = () => {
    return navigator.userAgent || navigator.vendor || window.opera;
  };

  const detectDevice = () => {
    const userAgent = getUserAgent();
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Touch device detection
    const touchDevice = 'ontouchstart' in window || 
                       navigator.maxTouchPoints > 0 || 
                       navigator.msMaxTouchPoints > 0;
    setIsTouch(touchDevice);

    // Orientation detection
    const isLandscape = width > height;
    setOrientation(isLandscape ? 'landscape' : 'portrait');

    // Device type detection based on user agent and screen size
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const tabletRegex = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)|Android(?=.*\bTablet\b)/i;
    
    const isMobileUA = mobileRegex.test(userAgent);
    const isTabletUA = tabletRegex.test(userAgent);

    // Screen size based detection (more reliable for responsive design)
    const isMobileSize = width < mobileBreakpoints.tablet;
    const isTabletSize = width >= mobileBreakpoints.tablet && width < mobileBreakpoints.desktop;
    const isDesktopSize = width >= mobileBreakpoints.desktop;

    // Combine user agent and screen size for better detection
    const mobile = isMobileUA || (isMobileSize && touchDevice);
    const tablet = isTabletUA || (isTabletSize && touchDevice && !mobile);
    const desktop = !mobile && !tablet;

    setIsMobile(mobile);
    setIsTablet(tablet);
    setIsDesktop(desktop);

    // Set screen size category
    if (width < breakpoints.sm) {
      setScreenSize('xs');
    } else if (width < breakpoints.md) {
      setScreenSize('sm');
    } else if (width < breakpoints.lg) {
      setScreenSize('md');
    } else if (width < breakpoints.xl) {
      setScreenSize('lg');
    } else {
      setScreenSize('xl');
    }
  };

  useEffect(() => {
    // Initial detection
    detectDevice();

    // Add resize listener with debouncing
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(detectDevice, 150);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      // Add small delay for orientation change to complete
      setTimeout(detectDevice, 300);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', detectDevice);
      clearTimeout(timeoutId);
    };
  }, []);

  // Utility functions for responsive design
  const isBreakpoint = (breakpoint) => {
    const width = window.innerWidth;
    return width >= breakpoints[breakpoint];
  };

  const isBetweenBreakpoints = (min, max) => {
    const width = window.innerWidth;
    return width >= breakpoints[min] && width < breakpoints[max];
  };

  const getDeviceType = () => {
    if (isMobile) return 'mobile';
    if (isTablet) return 'tablet';
    return 'desktop';
  };

  const shouldUseMobileLayout = () => {
    return isMobile || (isTablet && orientation === 'portrait');
  };

  const shouldUseBottomNavigation = () => {
    return isMobile || (isTouch && window.innerWidth < mobileBreakpoints.desktop);
  };

  const getOptimalImageSize = () => {
    if (isMobile) return 'small';
    if (isTablet) return 'medium';
    return 'large';
  };

  // Get appropriate font sizes for the device
  const getFontScale = () => {
    if (isMobile) return 0.9;
    if (isTablet) return 1.0;
    return 1.1;
  };

  // Check if device supports PWA installation
  const supportsPWA = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  };

  // Get safe area insets for devices with notches
  const getSafeAreaInsets = () => {
    const style = getComputedStyle(document.documentElement);
    return {
      top: style.getPropertyValue('--safe-area-inset-top') || '0px',
      right: style.getPropertyValue('--safe-area-inset-right') || '0px',
      bottom: style.getPropertyValue('--safe-area-inset-bottom') || '0px',
      left: style.getPropertyValue('--safe-area-inset-left') || '0px'
    };
  };

  return {
    // Device detection
    isMobile,
    isTablet,
    isDesktop,
    isTouch,
    
    // Screen information
    screenSize,
    orientation,
    
    // Utility functions
    isBreakpoint,
    isBetweenBreakpoints,
    getDeviceType,
    shouldUseMobileLayout,
    shouldUseBottomNavigation,
    getOptimalImageSize,
    getFontScale,
    supportsPWA,
    getSafeAreaInsets,
    
    // Breakpoints for external use
    breakpoints,
    mobileBreakpoints
  };
};

export default useMobileDetect;